import { COLORS } from "./constants";
import Icon from "./Icon";
import SketchColorPicker from "./SketchColorPicker";

const numInputStyle = {
  width: 80,
  padding: "10px 12px",
  fontSize: 15,
  border: "1.5px solid #d0d0d0",
  borderRadius: 8,
  outline: "none",
  textAlign: "center",
};

export default function TextEditModal({
  showTextModal,
  editingObj,
  textMode,
  textVal,
  textFt,
  textIn,
  textM,
  textCm,
  setTextMode,
  setTextVal,
  setTextFt,
  setTextIn,
  setTextM,
  setTextCm,
  setShowTextModal,
  setEditingObjId,
  updateSelectedObj,
  commitText,
  textFontSize, setTextFontSize,
  textBold, setTextBold,
  textAlign, setTextAlign,
  textHighlight, setTextHighlight,
  textHighlightColor, setTextHighlightColor,
  color, setColor,
  isTimestamp
}) {
  if (!showTextModal) return null;

  const isBold = editingObj ? editingObj.bold !== false : textBold;
  const isHighlighted = editingObj ? !!editingObj.highlight : !!textHighlight;
  const currentHlColor = (editingObj ? editingObj.highlightColor : textHighlightColor) || "#ffff00";
  const activeTextColor = (editingObj ? editingObj.color : color) || (isHighlighted ? "#000000" : "#ffffff");

  const handleHlColorChange = (c) => {
    if (setTextHighlightColor) setTextHighlightColor(c);
    if (editingObj) updateSelectedObj(editingObj.id, { highlightColor: c });
  };

  const handleTextColorChange = (c) => {
    if (setColor) setColor(c);
    if (editingObj) updateSelectedObj(editingObj.id, { color: c });
  };

  const close = () => {
    setShowTextModal(false);
    setEditingObjId(null);
    setTextVal("");
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center"}}
      onMouseDown={close}>
      <div onMouseDown={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:16,padding:24,width:440,boxShadow:"0 20px 60px rgba(0,0,0,0.4)"}}>
        <h2 style={{fontSize:20,fontWeight:600,marginBottom:16,color:"#000"}}>{editingObj ? (isTimestamp ? "Edit Timestamp" : "Edit Text") : (isTimestamp ? "Add Timestamp" : "Add Text")}</h2>
        
        {!isTimestamp && (
          <div style={{display:"flex",border:"1px solid #d0d0d0",borderRadius:8,overflow:"hidden",marginBottom:16}}>
            {[["abc","Abc"],["ftin","ft. in."],["mcm","m. cm."]].map(([mode,label])=>(
              <button key={mode} onClick={()=>setTextMode(mode)}
                style={{flex:1,padding:"9px 0",fontSize:13,fontWeight:500,background:textMode===mode?"#1c3a5c":"transparent",color:textMode===mode?"#fff":"#333",border:"none",cursor:"pointer"}}>
                {label}
              </button>
            ))}
          </div>
        )}

        {isTimestamp ? (
          <input value={textVal} onChange={e=>setTextVal(e.target.value)} autoFocus placeholder="" onKeyDown={e=>e.key==="Enter"&&commitText()} style={{width:"100%",padding:"12px 14px",fontSize:16,border:"1.5px solid #d0d0d0",borderRadius:8,outline:"none",boxSizing:"border-box",fontWeight:isBold?"bold":"normal"}}/>
        ) : textMode === "abc" ? (
          <textarea
            value={textVal}
            onChange={e=>setTextVal(e.target.value)}
            autoFocus
            placeholder="Type text here (Enter for new line)..."
            rows={3}
            onKeyDown={e => {
              if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                e.preventDefault();
                commitText();
              }
            }}
            style={{
              width:"100%",
              minHeight: 80,
              padding:"10px 14px",
              fontSize:15,
              lineHeight: 1.4,
              border:"1.5px solid #d0d0d0",
              borderRadius:8,
              outline:"none",
              boxSizing:"border-box",
              fontWeight:isBold?"bold":"normal",
              background: "#fff",
              color: "#1c1c1c",
              resize: "vertical",
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
            }}
          />
        ) : null}

        {!isTimestamp && textMode==="ftin" && <div style={{display:"flex",gap:8,alignItems:"center"}}><input type="number" value={textFt} onChange={e=>setTextFt(e.target.value)} style={numInputStyle} placeholder="0"/><span style={{color:"#333",fontWeight:500}}>ft.</span><input type="number" value={textIn} onChange={e=>setTextIn(e.target.value)} style={numInputStyle} placeholder="0"/><span style={{color:"#333",fontWeight:500}}>in.</span></div>}
        {!isTimestamp && textMode==="mcm" && <div style={{display:"flex",gap:8,alignItems:"center"}}><input type="number" value={textM} onChange={e=>setTextM(e.target.value)} style={numInputStyle} placeholder="0"/><span style={{color:"#333",fontWeight:500}}>m.</span><input type="number" value={textCm} onChange={e=>setTextCm(e.target.value)} style={numInputStyle} placeholder="0"/><span style={{color:"#333",fontWeight:500}}>cm.</span></div>}

        <div style={{marginTop:16,paddingTop:14,borderTop:"1px solid #eee"}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
            <span style={{fontSize:12,color:"#555",width:32}}>Size</span>
            {(() => {
              const currentSize = (() => {
                if (editingObj && editingObj.type === "text") {
                  const dx = editingObj.x2 - editingObj.x1;
                  const dy = editingObj.y2 - editingObj.y1;
                  const len = Math.hypot(dx, dy) || 1;
                  const textStr = editingObj.text || "";
                  const maxLineChars = Math.max(...textStr.split("\n").map(l => l.length), 1);
                  return editingObj.fontSize || Math.max(10, Math.min(200, len / (maxLineChars * 0.55)));
                }
                return editingObj ? (editingObj.fontSize || 22) : textFontSize;
              })();
              
              return (
                <>
                  <input type="range" min={10} max={200} value={currentSize}
                    onChange={e=>{
                      const fs = Number(e.target.value);
                      if (editingObj && editingObj.type === "text") {
                        const textStr = editingObj.text || "";
                        const maxLineChars = Math.max(...textStr.split("\n").map(l => l.length), 1);
                        const newLen = fs * maxLineChars * 0.55;
                        const dx = editingObj.x2 - editingObj.x1;
                        const dy = editingObj.y2 - editingObj.y1;
                        const ang = Math.atan2(dy, dx);
                        updateSelectedObj(editingObj.id, {
                          fontSize: fs,
                          x2: editingObj.x1 + newLen * Math.cos(ang),
                          y2: editingObj.y1 + newLen * Math.sin(ang)
                        });
                      } else if (editingObj) {
                        updateSelectedObj(editingObj.id, {fontSize: fs});
                      } else {
                        setTextFontSize(fs);
                      }
                    }}
                    style={{flex:1,accentColor:"#007AFF"}}/>
                  <span style={{fontSize:12,color:"#555",width:45,textAlign:"right"}}>{Math.round(currentSize)}px</span>
                </>
              );
            })()}
          </div>

          <div style={{display:"flex",gap:8,marginBottom:12}}>
            <button onClick={()=>{
              if (editingObj) updateSelectedObj(editingObj.id,{bold: !isBold});
              else setTextBold(!isBold);
            }} title="Bold"
              style={{width:40,padding:"8px 0",borderRadius:6,border:"1.5px solid #d0d0d0",background:isBold?"#1c1c3a":"#fff",color:isBold?"#fff":"#333",fontWeight:700,fontSize:14,cursor:"pointer"}}>B</button>
            {["left","center","right"].map(a => (
              <button key={a} onClick={()=>{
                if (editingObj) updateSelectedObj(editingObj.id,{align:a});
                else setTextAlign(a);
              }} title={a}
                style={{width:40,padding:"8px 0",borderRadius:6,border:"1.5px solid #d0d0d0",background:(editingObj?(editingObj.align||"center"):textAlign)===a?"#1c1c3a":"#fff",color:(editingObj?(editingObj.align||"center"):textAlign)===a?"#fff":"#333",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
                <Icon id={a==="left"?"alignleft":a==="center"?"aligncenter":"alignright"} size={14}/>
              </button>
            ))}
            {!isTimestamp && (
              <button
                type="button"
                onClick={() => {
                  const nextHl = !isHighlighted;
                  if (setTextHighlight) setTextHighlight(nextHl);
                  if (nextHl) {
                    if (setColor) setColor("#000000");
                    if (editingObj) {
                      updateSelectedObj(editingObj.id, {
                        highlight: true,
                        highlightColor: currentHlColor || "#ffff00",
                        color: "#000000"
                      });
                    }
                  } else {
                    if (editingObj) {
                      updateSelectedObj(editingObj.id, { highlight: false });
                    }
                  }
                }}
                title="Highlight Text Background"
                style={{
                  flex: 1,
                  padding: "8px 10px",
                  borderRadius: 6,
                  border: isHighlighted ? `2px solid ${currentHlColor}` : "1.5px solid #d0d0d0",
                  background: isHighlighted ? currentHlColor : "#fff",
                  color: "#000",
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  transition: "all 0.2s ease",
                  boxShadow: isHighlighted ? `0 0 10px ${currentHlColor}88` : "none",
                }}
              >
                <span style={{ fontSize: 13 }}>🖍</span>
                <span>Highlight</span>
              </button>
            )}
          </div>

          {!isTimestamp && isHighlighted && (
            <div style={{ marginBottom: 14, background: "#f8f9fa", padding: "8px 10px", borderRadius: 8, border: "1px solid #e9ecef" }}>
              <div style={{ fontSize: 11, color: "#666", marginBottom: 6, fontWeight: 600 }}>Highlight Color</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {COLORS.map(c => (
                  <button key={c} onClick={() => handleHlColorChange(c)}
                    style={{
                      width: 20, height: 20, borderRadius: "50%", background: c,
                      border: currentHlColor === c ? "2.5px solid #007AFF" : c === "#ffffff" ? "1.5px solid #ccc" : "1.5px solid transparent",
                      cursor: "pointer"
                    }}
                  />
                ))}
                <SketchColorPicker
                  color={currentHlColor}
                  size={20}
                  title="Custom Highlight Color"
                  onChange={handleHlColorChange}
                />
              </div>
            </div>
          )}

          <div style={{ fontSize: 11, color: "#666", marginBottom: 6, fontWeight: 600 }}>Text Color</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 18 }}>
            {COLORS.map(c => (
              <button key={c} onClick={() => handleTextColorChange(c)}
                style={{
                  width: 20, height: 20, borderRadius: "50%", background: c,
                  border: activeTextColor === c ? "2.5px solid #007AFF" : c === "#ffffff" ? "1.5px solid #ccc" : "1.5px solid transparent",
                  cursor: "pointer"
                }}
              />
            ))}
            <SketchColorPicker
              color={activeTextColor}
              size={20}
              title="Custom Text Color"
              onChange={handleTextColorChange}
            />
          </div>
        </div>

        <div style={{display:"flex",justifyContent:"flex-end",gap:10,marginTop:8}}>
          <button onClick={close} style={{padding:"10px 20px",borderRadius:8,border:"1px solid #d0d0d0",background:"transparent",fontSize:14,cursor:"pointer",color:"#333"}}>Cancel</button>
          <button onClick={commitText} style={{padding:"10px 24px",borderRadius:8,border:"none",background:"#007AFF",color:"#fff",fontSize:14,fontWeight:600,cursor:"pointer"}}>Save</button>
        </div>
      </div>
    </div>
  );
}

